import React from 'react'
import styled from '@emotion/styled'
import { Integration } from './types'
import ConnectedIcon from 'packages/@slerp/assets/integrations/connected.svg'
import DisconnectedIcon from 'packages/@slerp/assets/integrations/disconnected.svg'
import { CommonButton } from 'components/Common'
import { useNavigate } from 'react-router-dom'

interface IntegrationCardProps {
  integration: Integration
}

const IntegrationCard = ({ integration }: IntegrationCardProps) => {
  const {
    active: isActive,
    name,
    description,
    icon,
    slug,
    connectedStores,
    unconnectedStores
  } = integration
  const navigate = useNavigate()

  const storeCountText = (count: number) => {
    return count === 1 ? 'Store' : 'Stores'
  }

  const showCounts = isActive && (connectedStores > 0 || unconnectedStores > 0)

  return (
    <Root>
      <Card active={isActive}>
        <CardIcon src={icon} />
        <CardContent>
          {isActive && (
            <StatusTag>
              <span>Active</span>
            </StatusTag>
          )}
          <CardName>{name}</CardName>
          <CardDescription>{description}</CardDescription>
        </CardContent>
        {isActive ? (
          <CommonButton
            onClick={() => {
              navigate(`/integrations/${slug}`)
            }}
          >
            Manage
          </CommonButton>
        ) : (
          <CommonButton
            onClick={() => {
              navigate(`/integrations/${slug}`)
            }}
          >
            Connect
          </CommonButton>
        )}
      </Card>
      {showCounts && (
        <Connections>
          {connectedStores > 0 && (
            <Connection>
              <ConnectionIcon src={ConnectedIcon} />
              <CardContent>
                <CardName size='sm'>
                  You have {connectedStores} Connected{' '}
                  {storeCountText(connectedStores)}{' '}
                </CardName>
                <CardDescription>
                  This means all orders automatically arrive in the POS,
                  reducing manual work, mistakes, and missed orders.
                </CardDescription>
              </CardContent>
            </Connection>
          )}
          {unconnectedStores > 0 && (
            <Connection>
              <ConnectionIcon src={DisconnectedIcon} />
              <CardContent>
                <CardName size='sm'>
                  You have {unconnectedStores} Unconnected{' '}
                  {storeCountText(unconnectedStores)}{' '}
                </CardName>
                <CardDescription>
                  Connecting them will streamline order handling and reduce the
                  need for manual entry.
                </CardDescription>
              </CardContent>
            </Connection>
          )}
        </Connections>
      )}
    </Root>
  )
}

const Root = styled.li`
  list-style: none;
  display: flex;
  flex-direction: column;

  border-radius: 4px;
  border: 1px solid #d2d2d9;
  background-color: #ffffff;
  box-shadow: 0px 5px 12px 0px #4a4a681a;
`

const ConnectionIcon = styled.img`
  width: 20px;
  height: 20px;
`

const Connections = styled.div`
  padding-top: 1px;
`

const Connection = styled.div`
  padding: 12px 0;
  margin: 0 16px;
  background-color: #ffffff;

  display: flex;
  align-items: center;
  gap: 12px;

  &:not(:last-of-type) {
    border-bottom: 1px solid #d2d2d9;
  }
`

const StatusTag = styled.div`
  width: fit-content;
  margin-bottom: 4px;

  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
  color: #4eb21c;

  padding: 5px 8px;
  border-radius: 24px;
  background-color: #4eb21c1a;
`

const Card = styled.div<{ active: boolean }>(
  ({ active }) => `
  padding: 12px 16px;

  display: flex;
  align-items: center;
  gap: 12px;

  border-radius: 4px;
  border: 1px solid #D2D2D9;
  margin: -1px;
  background-color: ${active ? '#F6FAFF' : '#FFFFFF'};

  >a.ant-btn {
    padding-top: 5px !important;
  }
`
)

const CardIcon = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 4px;
  object-fit: contain;
  object-position: center;
`

const CardContent = styled.div`
  flex: 1;
`

const CardName = styled.p<{ size?: 'sm' }>(
  ({ size }) => `
  font-size: ${size === 'sm' ? '12px' : '14px'};
  font-weight: ${size === 'sm' ? 500 : 600};
  line-height: 100%;
  color: #4A4A68;
  margin-bottom: 4px;
`
)

const CardDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 100%;
  color: #77778e;
  margin-bottom: 0;
`

export default IntegrationCard
